import {
	Component,
	OnDestroy,
	OnInit,
	ChangeDetectionStrategy,
} from '@angular/core';
import {
	MatButtonToggleChange,
	MatButtonToggleGroup,
	MatButtonToggle,
} from '@angular/material/button-toggle';
import { Router } from '@angular/router';
import { CellClickedEvent } from 'ag-grid-community';
import { ApplicationService } from '@lmc-app/core/services/application.service';
import { UserService } from '@lmc-app/core/services/user.service';
import { ApplicationCall } from '@lmc-app/shared/interface/application-call.interface';
import dayjs from 'dayjs';
import { User } from '@lmc-app/shared/interface/user.interface';
import { Subject, takeUntil } from 'rxjs';
import { Applicant } from '@lmc-app/shared/enum/applicant.enum';
import { ApplicationCallPurpose } from '@lmc-app/shared/enum/application-call-purpose.enum';
import { ApplicationCallStatus } from '@lmc-app/shared/enum/application-call-status.enum';
import { NgIf } from '@angular/common';
import { StateDisplayComponent } from '../../shared/components/state-display/state-display.component';
import { CustomTableComponent } from '../../custom-table/custom-table.component';

@Component({
	selector: 'app-applications-calls',
	templateUrl: './application-calls.component.html',
	changeDetection: ChangeDetectionStrategy.Eager,
	styleUrls: ['./application-calls.component.scss'],
	imports: [
		MatButtonToggleGroup,
		MatButtonToggle,
		NgIf,
		StateDisplayComponent,
		CustomTableComponent,
	],
})
export class ApplicationCallsComponent implements OnInit, OnDestroy {
	columns = [
		{
			headerName: 'Subject',
			field: 'subject',
			cellClass: 'subject-link',
			sortable: true,
			filter: 'agTextColumnFilter',
			autoHeight: true,
		},
		{
			headerName: 'Applicant',
			field: 'applicant',
			sortable: true,
			filter: 'agTextColumnFilter',
			autoHeight: true,
			valueGetter: (params: any) => {
				return Applicant.toDisplayText(params.data.applicant);
			},
		},
		{
			headerName: 'Purpose',
			field: 'purpose',
			sortable: true,
			filter: 'agTextColumnFilter',
			autoHeight: true,
			valueGetter: (params: any) => {
				return ApplicationCallPurpose.toDisplayText(params.data.purpose);
			},
		},
		{
			headerName: 'Date time',
			field: 'dateTime',
			sortable: true,
			filter: 'agTextColumnFilter',
			autoHeight: true,
			valueGetter: (params: any) => {
				return dayjs(params.data.dateTime).format('YYYY-MM-DD h:mm A');
			},
		},
		{
			headerName: 'Status',
			field: 'status',
			sortable: true,
			filter: 'agTextColumnFilter',
			autoHeight: true,
			valueGetter: (params: any) => {
				return ApplicationCallStatus.toDisplayText(params.data.status);
			},
		},
		{
			headerName: 'Owned by',
			field: 'ownedByEmployee.name',
			sortable: false,
			filter: 'agTextColumnFilter',
			autoHeight: true,
		},
	];
	public dropdownFilters = [
		{
			label: 'Status',
			filterColumn: 'status',
			filters: [
				{
					label: 'All',
					value: 'All',
					defaultSelected: true,
				},
				{
					label: 'Scheduled',
					value: 'SCHEDULED',
					defaultSelected: false,
				},
				{
					label: 'Cancelled',
					value: 'CANCELLED',
					defaultSelected: false,
				},
				{
					label: 'Completed',
					value: 'COMPLETED',
					defaultSelected: false,
				},
				{
					label: 'Called Customer – No Answer',
					value: 'CALLED_CUSTOMER_NO_ANSWER',
					defaultSelected: false,
				},
				{
					label: 'Called Customer – Partial Documents Uploaded',
					value: 'CALLED_CUSTOMER_PARTIAL_DOCUMENTS_UPLOADED',
					defaultSelected: false,
				},
				{
					label: 'Called Customer – All Documents Uploaded',
					value: 'CALLED_CUSTOMER_ALL_DOCUMENTS_UPLOADED',
					defaultSelected: false,
				},
			],
		},
	];
	currentUser!: User;
	callFilter: 'my' | 'all' = 'my';
	applicationCalls: ApplicationCall[] = [];
	filteredApplicationCalls: ApplicationCall[] = [];
	protected _onDestroy = new Subject<void>();

	// Loading states
	isLoading: boolean = false;

	constructor(
		private readonly applicationService: ApplicationService,
		private readonly userService: UserService,
		private readonly router: Router
	) {}

	ngOnInit(): void {
		this.isLoading = true;
		this.userService.user$
			.pipe(takeUntil(this._onDestroy))
			.subscribe((user) => {
				this.currentUser = user;
				this.applyFilter();
			});
		this.getApplicationCalls();
	}

	getApplicationCalls() {
		this.applicationService.queryApplicationCalls().subscribe({
			next: (res) => {
				this.applicationCalls = res;
				this.applyFilter();
				this.isLoading = false;
			},
			error: (error) => {
				console.error(error);
				this.isLoading = false;
			},
		});
	}

	onCellClicked(e: CellClickedEvent) {
		if (e.colDef.field !== 'subject') {
			return;
		}
		this.router.navigate([`applications/summary/${e.data.applicationId}`]);
	}

	onFilterChange(event: MatButtonToggleChange) {
		this.callFilter = event.value;
		this.applyFilter();
	}

	private applyFilter() {
		switch (this.callFilter) {
			case 'my': {
				this.filteredApplicationCalls = this.applicationCalls.filter(
					(applicationCall) =>
						applicationCall.ownedByEmployee?.userId ===
						this.currentUser?.employeeID
				);
				break;
			}
			case 'all': {
				this.filteredApplicationCalls = this.applicationCalls;
				break;
			}
		}
	}

	ngOnDestroy() {
		this._onDestroy.next();
		this._onDestroy.complete();
	}
}
